/**
 * Typed API errors mirrored from `src/api/errors/index.ts` on the backend.
 *
 * The server's global error handler serializes every `AppError` subclass
 * as `{ error: { code, message, details? } }` with the matching HTTP
 * status code. We wrap that payload in a single `ApiError` class so
 * callers can `instanceof`-match and branch on the stable `code` string
 * without ever string-matching the message.
 */

/**
 * Stable error codes the backend emits. Kept in sync with
 * `src/api/errors/index.ts`. Adding a new code on the backend is a
 * one-line addition here.
 */
export type ApiErrorCode =
  | 'deal_not_found'
  | 'unauthorized_actor'
  | 'invalid_state_transition'
  | 'idempotency_conflict'
  | 'blockchain_error'
  | 'validation_error'
  | 'non_custodial_violation'
  | 'rate_limited'
  | 'network_error'
  | 'unknown_error';

export class ApiError extends Error {
  public readonly code: ApiErrorCode;
  public readonly status: number;
  public readonly details: unknown;

  constructor(code: ApiErrorCode, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /** Narrowing helper: is this one of the given codes? */
  is(...codes: ApiErrorCode[]): boolean {
    return codes.includes(this.code);
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
